// Students and families: the shape of the documents, and the vocabulary they
// share — one contact vocabulary, one place that says what a document holds,
// and derived values derived rather than stored.
//
// Pure, like the rest of domain/ — no Firebase here, so the same normalisation
// runs in a Cloud Function or a test without a browser.
//
// How a name is *written* is not decided here. It is a studio setting — MMS's
// Naming Format, three choices offered separately for Teacher, Student and
// Parent — so the vocabulary and the functions that apply it live in
// domain/studio.js and every display function below takes one, defaulting to
// the studio default.
//
// ── The rules are `core/src/people.rs` ─────────────────────────────────────
//
// What a student, a family and a contact are — the normalisers and their patch
// forms, the three refusals, the two deletion plans and the family one, the
// card heading, the defaults — are the Rust core's, so the Android and iOS
// screens build the same documents and refuse the same edits from the same
// implementation. `core/src/people_vectors.json` holds the recorded answers,
// replayed there and through this adapter by `test/core/people.wasm.test.js`.
//
// **What is left here is the boundary and the web's own vocabulary.** The
// boundary is below: a form's loose values coerced (`String(v)`, `Boolean(v)`,
// `Number(v)`), and the core's rows turned back into the plain objects every
// caller receives. The vocabulary is the constants a `maxLength` or a dialog
// reads at module scope, before the core can have been loaded — pinned to the
// core's own by `test/domain/peopleCore.test.js` — plus the one-off #319
// migration planner and the console diagnostic for a probe that gave up.
//
// The long arguments are beside the constant or the function they explain; the
// Rust carries short versions and points here.

// `.js`, not extensionless: functions/delivery/templates.js loads the generated
// mirror of this file under Node's ESM resolver, which does not guess
// extensions. Vite resolves both, so an extensionless import here builds clean
// and fails only on deploy.
import { DEFAULT_NAME_FORMATS, NAME_FORMATS } from './studio.js';
// `./core.js`, not `../lib/core.js`: this file is copied verbatim into
// functions/domain/, and that door is inside the copy (#218).
import { coreSync } from './core.js';
import { documentFromRows, documentToRows } from './documents.js';

// A person's contact details, spelled the same way wherever they appear.
//
// `mobile` is deliberately not a field here: a **student** has one number, and
// no screen distinguishes a mobile from a landline on one.
//
// A family *contact* carries three numbers, each with its own SMS tick, because
// MMS's contact drawer does — see CONTACT_NUMBERS and `normaliseFamilyContact`
// further down.
export const CONTACT_FIELDS = ['email', 'phone', 'smsCapable'];

// ── The boundary ────────────────────────────────────────────────────────────
//
// The phones *write* these documents, so "what an emptied field is stored as"
// is a rule three platforms have to agree on, and `people::text` in the core
// is where it is decided. It trims with JavaScript's white space rather than
// Rust's (`js_compat::js_trim`).
//
// What is here is coercion: a form hands over whatever its controls hold, the
// core takes typed records, and the conversion is `String(v)` for text (so
// `42` in a name field is `"42"`, and `null` is the empty field),
// `Boolean(v)` for a tick, `Number(v)` for the duration, and a tag field that
// is either a list or a comma-separated string.
//
// **A key the form does not carry is left out of the record**, and the core
// reads its absence as "no opinion" — which is how one input shape serves a
// whole document, a patch and a refusal. A patch asks `hasOwnProperty`; a
// refusal asks `in`.

const hasOwn = (o, k) => Object.prototype.hasOwnProperty.call(o, k);
const has = (o, k) => k in o;

const asText = (v) => (v == null ? '' : String(v));
// The email refusal reads only a string, and treats anything else as an empty
// field; `text()` reads anything.
const asEmailForRefusal = (v) => (typeof v === 'string' ? v : '');
// A list is one tag per entry; a string is split on commas. `['a,b']` is one
// tag and `'a,b'` is two.
const asTags = (v) => (Array.isArray(v) ? v.map((x) => String(x)) : String(v || '').split(','));
const COERCE = { text: asText, bool: Boolean, tags: asTags, number: Number };

// The input records' fields and how each is coerced — the core's
// `StudentInput`, `FamilyInput` and `FamilyContactInput`, in the document's
// order, which does not matter to the boundary.
const STUDENT_INPUT = Object.freeze([
  ['firstName', 'text'], ['lastName', 'text'], ['studentType', 'text'], ['status', 'text'],
  ['email', 'text'], ['phone', 'text'], ['smsCapable', 'bool'], ['studentSince', 'text'],
  ['gender', 'text'], ['birthday', 'text'], ['school', 'text'], ['referrer', 'text'],
  ['skillLevel', 'text'], ['instrumentTags', 'tags'], ['groupTags', 'tags'], ['note', 'text'],
  ['emailReminders', 'bool'], ['smsReminders', 'bool'], ['defaultDurationMin', 'number'],
  ['categoryId', 'text'], ['categoryLabel', 'text'], ['familyId', 'text'],
]);
const FAMILY_INPUT = Object.freeze([['lastName', 'text'], ['timeZone', 'text']]);
const CONTACT_INPUT = Object.freeze([
  ['familyId', 'text'], ['title', 'text'], ['firstName', 'text'], ['lastName', 'text'],
  ['email', 'text'], ['address', 'text'], ['note', 'text'], ['isInvoiceRecipient', 'bool'],
  ['emailReminders', 'bool'], ['smsReminders', 'bool'], ['mobile', 'text'],
  ['mobileSmsCapable', 'bool'], ['home', 'text'], ['homeSmsCapable', 'bool'], ['work', 'text'],
  ['workSmsCapable', 'bool'],
]);

function inputOf(spec, source, present, refusal = false) {
  const out = {};
  for (const [key, kind] of spec) {
    if (!present(source, key)) continue;
    out[key] = refusal && key === 'email' ? asEmailForRefusal(source[key]) : COERCE[kind](source[key]);
  }
  return out;
}

// `documentFromRows` and `documentToRows` are `./documents.js`'s, re-exported
// so every caller keeps its import.
export { documentFromRows, documentToRows };

// A refusal list as the `{ field: sentence }` object the drawers render.
const errorsOf = (errors) => Object.fromEntries(errors.map((e) => [e.field, e.message]));

/// The three input records, for a caller outside this file that has to hand
/// the core the record this file would — `test/ui/peopleDocuments.test.jsx`
/// holds the phones' builders to the browser's writes through these.
export const studentInput = (source = {}) => inputOf(STUDENT_INPUT, source, hasOwn);
export const familyInput = (source = {}) => inputOf(FAMILY_INPUT, source, hasOwn);
export const familyContactInput = (source = {}) => inputOf(CONTACT_INPUT, source, hasOwn);

// ── Two student defaults ────────────────────────────────────────────────────
//
// "A student with no status is active", and the student's lesson length: these
// two functions are the single definition, imported by every site below and in
// `src/data/`, `src/hooks/` and `src/components/`. Whitespace is not a status:
// the read and the write path both trim.
//
// The rules are `people::student_status` and `people::student_duration_min`
// in `core/`, because the phones build student documents too and a default is
// part of the document.

/// A student with no status is active.
///
/// The defaulting call sites are the write path in `normaliseStudent` below and
/// the read path in `src/data/useStudents.jsx`; the rest are defensive over a
/// value one of those has already defaulted, and still matter to a caller (or
/// a test) that hands in a raw document.
///
/// `validStudent` in firestore.rules is `optionalEnum(d, 'status', …)` over the
/// set in `src/components/students/columns.js` (#177, `lead` included), so a
/// stored status is one of those or absent; the trim is defensive.
export function studentStatus(value) {
  return coreSync().studentStatus(value == null ? null : String(value));
}

/// The student's lesson length, in minutes — and there is no default.
///
/// **This number prices lessons and divides make-up credits.**
///
/// MMS refuses an empty Duration on create and on edit (`Please enter a valid
/// duration`, range 1–1440, the wizard pre-filling 30), and `firestore.rules`
/// refuses one here too (#352).
///
/// So this returns `null` rather than a number the studio did not choose, and
/// `null` means **this document was not written by this app under the current
/// rule.** Every reader says what it does with that; in `creditSummary` in
/// ./makeup.js the number is a denominator, so a missing length is a balance
/// that cannot be expressed rather than a balance of zero.
///
/// Who reads it:
///
///   * `src/data/useStudents.jsx` renames it on the way in — stored
///     `defaultDurationMin`, read `durationMin` — which is why a grep for
///     either name finds only half the pair.
///   * `src/hooks/useEventForm.js` seeds a **new** event's duration from it
///     where the attendees agree (#241); its filter is already
///     `Number.isFinite(d) && d > 0`, so a null seeds nothing.
///   * `price_occurrence` in `core/src/pricing.rs` charges `rate * minutes / 60`
///     for an hourly student, by way of the event that was seeded.
///   * the list's Duration column, the detail sidebar, the Student Details
///     report's cell, and the edit drawer's prefill.
///
/// The other 30 in this app is a different one and is **not** affected:
/// `expand` in `core/src/schedule.rs` folds an occurrence with no duration of
/// its own to 30 (`.unwrap_or(30)`). That is a lesson's missing duration, not a
/// student's, and `validEventDoc.durationMin` is still optional.
export function studentDurationMin(value) {
  return coreSync().studentDurationMin(Number(value)) ?? null;
}

// The student's `email`, `phone` and `smsCapable` are normalised in the core
// with the rest of the student document. The number is stored as the address
// that will be dialled, punctuation stripped and country code required;
// ./phone.js has the argument, including where MyMusicStaff does the opposite.

// How long a student's private note may be.
//
// `65535`, not `65536`, and the rule beside it is `size() < 65536` — the
// `size() < N` convention every bound in firestore.rules uses, so the client
// stops one character short of what the database accepts. Same shape as
// NOTE_BODY_MAX in src/domain/lessonNotes.js and TEMPLATE_BODY_MAX in
// src/domain/noteTemplates.js.
//
// ── The same number as the lesson note, with different arithmetic ──────────
//
// #93 set the lesson-note bound by what three note bodies sharing one
// attendance document can occupy against Firestore's 1 MiB ceiling. A student
// note is one field on its own document, so the sum is redone here. Two
// constraints, and neither binds:
//
// **The document ceiling.** Firestore counts bytes — the path, every field
// name, every value, plus 32 bytes of overhead — against 1,048,576 per
// document. Worst case, four-byte UTF-8 throughout:
//
//     1 note x 65,535 characters x 4 bytes  =  262,140 bytes
//     everything else on the document       <   16,384 bytes  (reserved)
//     ─────────────────────────────────────────────────────
//     worst case                               278,524 bytes  =  0.27 MiB
//
// leaving three quarters of the ceiling spare. Four bytes per character is
// double the truth (#93): `maxLength` counts UTF-16 code units and a four-byte
// UTF-8 character costs two of them, so the real ceiling is three bytes per
// unit.
//
// The 16 KiB reservation is a bound because #121 bounded every field. The worst
// case every field is allowed to reach, at the same four bytes per character:
//
//     names, contact, free text     firstName 200, lastName 200, email 320,
//                                   phone 16, gender 100, school 200,
//                                   referrer 200, skillLevel 100,
//                                   categoryLabel 100, categoryId 200,
//                                   familyId 200            = 1,836 characters
//     the two date-only strings     studentSince, birthday  =    20 characters
//     the three closed sets         studentType, status,
//                                   pricingMode             =    27 characters
//     the two tag lists             500 characters each     = 1,000 characters
//     ─────────────────────────────────────────────────────────────────────
//     2,883 characters x 4 bytes                            = 11,532 bytes
//     field names, ~22 of them                              <    500 bytes
//     two numbers and two timestamps                        <     64 bytes
//     the document path and Firestore's 32 bytes of overhead <   200 bytes
//     ─────────────────────────────────────────────────────────────────────
//     everything that is not the note                       < 12,300 bytes
//
// Inside the 16,384 reserved, with about 4 KiB spare for the next field.
//
// **The phone.** The Android app lists this collection whole — 500 documents
// under STUDENT_LIMIT in StudioRepository.kt — and `Names.Student.fromMap`
// reads `note` into memory for every one of them. Worst case 500 x 65,535 =
// 32.8 M characters, thirty times smaller than what #93 accepted on the same
// device (attendance listed at 5,000 documents carrying three bodies each).
//
// ── Bounded, where MMS is not ──────────────────────────────────────────────
//
// MMS applies no length limit to a student profile note, and its profile-note
// drawer (a plain `<textarea>`, a different surface from the lesson note,
// #115) has no counter; neither does ours. 65,535 is tighter than MMS, set by
// the phone above.
//
// MMS's Notes card clamps a long note to about five lines behind a `Show More`
// link, and so does ours — `NoteBody` in components/students/StudentSidebar.jsx,
// #136. See the note at `case 'notes'` in components/students/columns.js for
// the list's Notes column. That is a rendering bound, not this one.
//
// A student document with no ceiling could be made unwritable: `createStudent`
// writes the student and the family in one batch, and a document Firestore
// refuses takes the family account with it.
//
// **Not clamped on write, unlike the lesson note.** `setNote` in
// hooks/useAttendanceForm.js clamps because `applyTemplate` *appends*
// programmatically. Nothing appends to a student note — the textarea's
// `maxLength` is the only way text gets in — so a clamp would only silently
// truncate a value a caller passed deliberately (#93); the rule refuses it
// instead.
export const STUDENT_NOTE_MAX = 65535;

// How long a student's two tag lists may be (#121): a count and a total.
//
// `instrumentTags` and `groupTags` are the only *lists* on this document, and
// bounding one axis is not bounding the list, so `firestore.rules` enforces
// both — see optionalTextList there for why the second number is the *total*
// across entries (rules cannot iterate a list).
//
// **20 entries**, past any real roster — the same argument
// `optionalList(d, 'studentIds', 30)` makes about a group class.
//
// **499 characters in total**: the rule is written `size() < 500`, so the
// longest value it accepts is 499, and the client stops exactly there.
//
// The count is the half a `maxLength` cannot express, so `tagsWithinBounds`
// below is what the wizard gates Next on, and the field shows the reason
// (#115): a bound only in the rules is a save that fails with nothing on
// screen to say why.
//
// Both are pinned to `optionalTextList(d, 'instrumentTags', …)` and its
// `groupTags` twin by `test/domain/people.test.js`, which also reads the
// helper's two comparisons — `<=` on the count, `<` on the characters (#341).
export const STUDENT_TAGS_MAX = 20;
export const STUDENT_TAG_CHARS_MAX = 499;

// The tags a form field holds, as the document will store them. Exported
// because two callers need to agree about it — the normaliser below, and the
// wizard, which has to know whether what has been typed is storable *before*
// Firestore refuses it.
export function studentTags(value) {
  return coreSync().studentTags(asTags(value));
}

// Whether a tag field's contents fit what firestore.rules will take.
//
// Both axes. A predicate rather than a clamp: `normaliseStudent` does not
// truncate a note and does not drop a tag either (#93), so a teacher who pasted
// twenty-five groups is told rather than quietly given twenty.
export function tagsWithinBounds(value) {
  return coreSync().tagsWithinBounds(asTags(value));
}

// The fields a student document holds. Anything not listed is dropped on write
// rather than persisted.
//
// The list and its defaults are `people::normalise_student`'s: `studentType`
// 'child', `status` 'active', `categoryLabel` 'Lesson', the number stripped to
// the address that will be dialled, both tag lists `[]`, and
// `defaultDurationMin` null rather than a thirty nobody chose.
//
// **Both halves of the category, as the event series stores them (#110).** The
// id is the one that can be followed: a rename, a recolour or a reminder-lead
// change reaches a document by `categoryId` and never by its label, because two
// studios' "Lesson" are not the same thing. The label is denormalised beside it
// so the student list, the sidebar and the report runner can name a category
// without joining a second collection.
//
// `categoryId` is nullable: a student written before the wizard took a picker
// has a label and no id, and null says "outside the managed list" rather than
// guessing which category was meant. `scripts/backfill-category-ids.mjs` is the
// one-off that offers to resolve them. The label keeps its 'Lesson' default — a
// name has to be something; `colorFor` in data/useCategories.jsx makes the same
// distinction.
export function normaliseStudent(input = {}) {
  return documentFromRows(coreSync().normaliseStudent(inputOf(STUDENT_INPUT, input, hasOwn)));
}

// ── What an edit may write, and what it refuses (issue #120) ───────────────
//
// What the edit drawers need that a create path does not: a per-field ceiling
// to hang a `maxLength` on, a refusal message keyed by field, and a way to
// write *some* of a document without the unlisted fields being read as "clear
// this".

// The per-field text ceilings, each one short of the rule in front of it.
//
// Same convention as STUDENT_NOTE_MAX and STUDENT_TAG_CHARS_MAX above: every
// clause in firestore.rules is `size() < N`, so the longest value it accepts
// is N - 1 and a `maxLength` stops exactly there (#121, #115).
// test/domain/peopleEdits.test.js asserts each against the clause it mirrors.
export const STUDENT_TEXT_MAX = Object.freeze({
  firstName: 199,
  lastName: 199,
  email: 319,
  gender: 99,
  school: 199,
  referrer: 199,
  skillLevel: 99,
});

// The family's one, from `validFamily`. `lastName` is the family's own name —
// what an invoice is addressed to and what the families list sorts by — and it
// is the only text field left on the document.
//
// Contact fields' ceilings are in `FAMILY_CONTACT_TEXT_MAX` below (#319).
// An object rather than a bare number so the call sites read the same as
// `STUDENT_TEXT_MAX`'s.
export const FAMILY_TEXT_MAX = Object.freeze({
  lastName: 199,
});

// The sentence under a field that was left empty.
//
// MMS says two different things for this (#120): clearing First Name in the
// profile drawer gives "Please enter a name", and in the Family Contact drawer
// "This field is required". This uses the second everywhere: `REQUIRED` in
// hooks/useExpenseEntryForm.js is the same string, MMS's on the expense drawer
// (#116).
export const REQUIRED_FIELD = 'This field is required';

// The email address is the one on the document the reminder worker reads, so
// the shape is checked rather than trusted — `optionalEmail` in firestore.rules
// is `^[^@\s]+@[^@\s]+$` and this is that regex, deliberately.
//
// Loose on purpose: the only check that means anything is whether mail
// arrives. What this refuses is what a person types by mistake — a name with no
// `@`, two addresses pasted into one box, a space in the middle — and it
// refuses it *here* rather than as a permission error with nothing on screen
// naming the field.
//
// The shape is checked by `people::email_error`, written out by hand
// because the core has no regex engine: one `@`, something either side, and
// no white space — JavaScript's white space, as `\s` meant here.
//
// `REQUIRED_FIELD` above and `EMAIL_INVALID` below are the core's
// `people::message` table's `required` and `email_invalid`, kept as constants
// because a test and a drawer read them at module scope, before the core can
// have been loaded; `test/domain/peopleCore.test.js` holds each to the table.
export const EMAIL_INVALID = 'Enter a valid email address.';

// Null when there is nothing wrong, which includes empty: every email field in
// this app is optional, in the rules and on every form, exactly as every phone
// field is. See phoneError in ./phone.js, which this mirrors.
export function emailError(raw) {
  const refusal = coreSync().emailError(asEmailForRefusal(raw));
  return refusal ? refusal.message : null;
}

// Everything an edit of a student will refuse, keyed by the field it belongs
// under. Pure, so the rule can be exercised without a drawer.
//
// **Keyed on presence, not on the whole document.** MMS spreads a student
// across four drawers and each one owns a slice, so a key absent from `form` is
// a key this call has no opinion about. `'firstName' in form` is the test
// rather than truthiness — an empty string is a field the teacher cleared.
//
// ── This is not `canSave` ──────────────────────────────────────────────────
//
// The button stays clickable and the refusal appears under the field
// (`entryErrors` in hooks/useExpenseEntryForm.js), as in MMS's profile drawer,
// where Save is never greyed. MMS's *other* student drawer greys Save until
// something changes; that is not copied, so one discipline holds.
export function studentEditErrors(form = {}) {
  // In the order a drawer shows them: both names — `lastName` being the one the database itself insists
  // on, First Name because MMS requires it — then the email, #90's E.164
  // refusal on the one surface where a number that is already wrong can be
  // corrected, #121's two-axis tag bound (the count half is what a `maxLength`
  // cannot express), and #115's note ceiling, for the value a `maxLength`
  // cannot catch because something other than typing put it there.
  return errorsOf(coreSync().studentEditErrors(inputOf(STUDENT_INPUT, form, has, true)));
}

// What an edit of the **family account itself** will refuse, which is one
// field; a contact's fields are `familyContactRecordErrors`' (#319).
//
// **`lastName` is the family's own name**, not any contact's — the string an
// invoice is addressed to and the one the families list sorts by. It is
// required because `familyDisplayName` falls back to the literal word "Family"
// without it. MMS has no such field: its family has no editable properties and
// its displayed name *is* its contact's name (#120). See
// `EditFamilyDrawer.jsx`.
export function familyNameErrors(form = {}) {
  return errorsOf(coreSync().familyNameErrors(inputOf(FAMILY_INPUT, form, has)));
}

// What Save refuses on the Lesson Settings drawer. Here rather than in
// hooks/useStudentEdit.js so the phones' drawers can refuse the same.
//
// Both bounds are the ones the wizard's Step 3 gates on — #137 for the
// duration and #164 for the rate. The wizard holds Next shut; this leaves Save
// clickable and puts the sentence under the field (#116, `entryErrors` in
// useExpenseEntryForm.js).
//
// The three refusals are `people::lesson_errors`'s: a category is required;
// the duration is required (#352) — MMS refuses an emptied Duration on an
// existing student with the same sentence it refuses a new one — and is at
// most a day; and the rate is checked against the money ceiling and a floor of
// zero (`people::rate_error`) unless pricing is 'none', where the field is not
// shown and 0 is written. The wizard's Rate box asks `rateError`.
export function lessonSettingsErrors(form, currency) {
  const duration = form.durationMin;
  return errorsOf(coreSync().lessonErrors(
    form.categoryId ? String(form.categoryId) : null,
    typeof duration === 'string' ? duration : duration == null ? null : String(duration),
    typeof form.pricingMode === 'string' ? form.pricingMode : null,
    String(form.rate ?? ''),
    currency,
  ));
}

// The Rate box on the new-student wizard's step 3: the same refusal
// `lessonSettingsErrors` puts under the drawer's rate, `people::rate_error` —
// `optionalMinor`'s ceiling and its floor of zero.
export function rateError(text, currency) {
  const refusal = coreSync().rateError(String(text ?? ''), currency);
  return refusal ? refusal.message : null;
}

// Normalise an edit without inventing the rest of the document.
//
// `normaliseStudent` and `normaliseFamily` answer "what does a whole document
// look like". Run over a four-field drawer, they would write `studentType`
// 'child' and `categoryLabel` 'Lesson' for a student who is neither.
//
// So: normalise, then keep only what was handed in. The normalisers stay the
// single statement of what a field means — trimming, empty-to-null, the phone's
// E.164 storage form, a tag string becoming a list — and the patch stays the
// size of the drawer that produced it. A key the normaliser does not list is
// dropped here too, so a form's scratch fields (`rate` as a typed decimal, a
// dirty flag, an id) never become columns.
//
// The core's `normalise_student_patch` does this: the input record carries
// only the keys the form has, and the core writes only those, in the
// document's order.
export function normaliseStudentPatch(input = {}) {
  return documentFromRows(coreSync().normaliseStudentPatch(inputOf(STUDENT_INPUT, input, hasOwn)));
}

export function normaliseFamilyPatch(input = {}) {
  return documentFromRows(coreSync().normaliseFamilyPatch(inputOf(FAMILY_INPUT, input, hasOwn)));
}

// The longest family note firestore.rules accepts (issue #92).
//
// The third of the note bounds, beside NOTE_BODY_MAX in domain/lessonNotes.js
// and TEMPLATE_BODY_MAX in domain/noteTemplates.js, and the same number as
// both, so one length is accepted for every note. 65535 because the rule is
// written `size() < 65536`. The arithmetic is at NOTE_BODY_MAX; this document
// carries one note rather than three.
//
// ── Two notes, and only one is written ──────────────────────────────────────
//
// A family *contact*'s Private Note — `note` on the contact record (#176) — is
// bounded by this constant, with a `note` clause in `validFamilyContact`. MMS
// puts the note on the contact, and that is the one that exists here.
//
// **Nothing writes `families.notes`**, the note on the **family document**
// (#92). normaliseFamily below does not list it, so it is dropped on write. The
// bound in firestore.rules still applies; a control for it would put this on
// the textarea and add `notes` to the core's `people::normalise_family` (and to
// `FAMILY_INPUT` here).
export const FAMILY_NOTES_MAX = 65535;

// ── A family's contacts (#176) ──────────────────────────────────────────────
//
// **A family has many contacts in MyMusicStaff, and here.** The tab MMS puts
// on the family is a *list*, with `＋ Add Contact` above it and a `⋮` on each
// card. The field list below is MMS's Family Contact drawer's (#176).
//
// ── Where this differs from MMS ─────────────────────────────────────────────
//
// **1. This is a flat collection with a `familyId`, not a subcollection.** MMS's
// contacts hang off the family. These do not, because `paths.js` builds every
// path as `users/{uid}/{collection}/{id}`, and the read store, the offline
// queue's subject keys and `useCollectionWindow` all take a single collection
// name; a subcollection is a change to the read and write machinery.
//
// **2. Three numbers per person.** MMS's drawer has Mobile, Home and Work, each
// with its own SMS Capable tick. A **student** keeps one number (see
// `CONTACT_FIELDS`).
//
// **3. This is where every contact lives, and the family document holds none
// (#319).** Four things hold that together:
//
//   * `scripts/migrate-family-contacts.mjs` moves each family's inline contact
//     into this collection with `isInvoiceRecipient: true`;
//   * `familyBillingContact` above resolves "who does this account's post go
//     to" from the collection, and is the only thing that answers it;
//   * `functions/reminders/store.js` and `functions/delivery/firestoreStore.js`
//     read the collection, so the server resolves it the same way the client
//     does, out of the same function;
//   * `validFamily` in firestore.rules does not name the six fields, and
//     `normaliseFamily` does not write them.
//
// `families/{id}.lastName` stays on the family. It is the family's own name —
// the billing name on an invoice and the families list's sort key — and it is
// not any contact's. `EditFamilyDrawer.jsx` is its editor.
//
// **MMS's invoice-recipient flag:**
//
//   * `Set as Preferred Invoice Recipient` is a **checkbox inside the contact's
//     edit drawer**, first of four in a `Preferences` block, not an item on the
//     card's `⋮` — so it is a per-contact *field*, `isInvoiceRecipient` below.
//   * A contact carrying it gains a green **`Preferred Invoice Recipient`**
//     pill beside the name; every card is otherwise the same shape. Ours does
//     the same.
//   * **It is not exclusive**: two contacts can carry it at once, with no
//     warning. Which one MMS addresses an invoice to then is not known;
//     `familyBillingContact` below takes the first flagged contact in this
//     list's order.
//
// MMS also offers `⇄ Change Family`, `👤 Convert to Student` on the card's `⋮`,
// and `Quick Select` on Address (#176); this app does not.

/// The per-field ceilings, each one short of the clause in front of it in
/// `validFamilyContact`. Same convention as STUDENT_TEXT_MAX above.
///
/// `test/domain/familyContacts.test.js` pins each against its clause. **`email`
/// is pinned separately** (#341): the rule sends it through `optionalEmail`,
/// which carries its own length. `STUDENT_TEXT_MAX.email` is the other copy of
/// that number, chained to the helper by `test/domain/peopleEdits.test.js`.
export const FAMILY_CONTACT_TEXT_MAX = Object.freeze({
  title: 99,
  firstName: 199,
  lastName: 199,
  email: 319,
  address: 499,
});

/// The three numbers, in MMS's order, with the field that ticks each one.
///
/// A list rather than three hard-coded rows, because the drawer, the card, the
/// normaliser and the refusal all walk the same three.
export const CONTACT_NUMBERS = Object.freeze([
  { key: 'mobile', sms: 'mobileSmsCapable', label: 'Mobile Number' },
  { key: 'home', sms: 'homeSmsCapable', label: 'Home Number' },
  { key: 'work', sms: 'workSmsCapable', label: 'Work Number' },
]);

/// What a whole contact document looks like — `people::normalise_family_contact`.
///
/// `familyId` is normalised here rather than merged in by the caller: a contact
/// with no family is a row no screen can reach.
///
/// Three fields:
///
/// * **`note`** is the contact's private note, bounded by `FAMILY_NOTES_MAX`
///   (#92), on the record MMS puts it on.
/// * **`isInvoiceRecipient` defaults false**: a contact that silently made
///   itself the recipient would redirect a studio's invoices by being created.
///   `familyBillingContact` falls back to the first contact when none is
///   flagged, so "a family with contacts always has a recipient" is a property
///   of the resolver rather than of a default on a document.
/// * **`showInPortal` is not stored (#417).** MMS's drawer has the tick; this
///   record does not carry it (ours is the Family Portal, #543). The normaliser
///   drops the key, so a caller that sends one is ignored silently —
///   `saveContact` writes what this returns. `test/domain/familyContacts.test.js`
///   hands `showInPortal` in and asserts it does not come back out.
///
/// The three numbers are stored as `storablePhone` makes them, not as `text`
/// would: the address that will be dialled, which `firestore.rules` matches
/// against E.164 on all three.
export function normaliseFamilyContact(input = {}) {
  return documentFromRows(coreSync().normaliseFamilyContact(inputOf(CONTACT_INPUT, input, hasOwn)));
}

export function normaliseFamilyContactPatch(input = {}) {
  return documentFromRows(coreSync().normaliseFamilyContactPatch(inputOf(CONTACT_INPUT, input, hasOwn)));
}

/// Everything an edit of a contact will refuse, keyed by field.
///
/// Keyed on presence like `studentEditErrors`, so a form that does not carry a
/// field has no opinion about it.
///
/// **First and Last are the only two MMS marks as required** — every other row
/// on its drawer says `Optional`. This adds refusals MMS does not make, for the
/// reason src/domain/phone.js gives (#90): MMS saves a malformed email and a
/// mobile number of `12` without comment, and our transport will not dial what
/// it would.
///
/// The family document's own field is `familyNameErrors`' (#319).
export function familyContactRecordErrors(form = {}) {
  return errorsOf(coreSync().familyContactRecordErrors(inputOf(CONTACT_INPUT, form, has, true)));
}

/// A contact record's own name, for a card heading —
/// `coda_domain::names::contact_card_name`.
///
/// Not `billingContactName` further down, which puts a contact's name through
/// the core's naming rules for a message greeting. This is a heading on a card
/// in the teacher's own screen, so it is not routed through the studio's naming
/// formats, and it leads with `title`, as MMS's cards do.
///
/// Falls back rather than returning an empty string, because a card with no
/// heading reads as a rendering fault. A part that is not a string is no part.
export function contactCardName(contact) {
  if (!contact) return 'Contact';
  const part = (v) => (typeof v === 'string' ? v : null);
  return coreSync().contactCardName(part(contact.title), part(contact.firstName), part(contact.lastName));
}

/// The contacts on one family, in the order the tab draws them — the shared
/// core's `coda_domain::names::family_contacts` (#394), where the reasoning is.
///
/// Sorted by last name then first, and **the invoice recipient is not floated
/// to the top**: MMS badges the card and leaves it where it falls.
///
/// **The comparison is the core's `locale_compare`, not `localeCompare`.** It
/// reproduces ICU's root collation over Latin text and diverges for a handful
/// of letters, listed in `core/src/js_compat.rs` with a test asserting it. So
/// the phone and the browser agree about who a studio's invoices are addressed
/// to, and a browser in a locale with its own collation (Swedish `ö` after
/// `z`) does not get that locale's order.
export function familyContacts(contacts = [], familyId) {
  return coreSync()
    .familyContacts(contacts.map(contactRef), familyId || null)
    .map((i) => contacts[i]);
}

/// The five fields the core reads off a contact, and nothing else.
///
/// A projection rather than the record: the rule reads four fields plus the
/// flag. It also keeps the *record* on this side — `familyContacts` answers
/// with positions, so what comes back is the caller's own object, not one
/// rebuilt from what crossed (a round trip could lose a phone number).
///
/// **A field that is not a string is absent**, since the core's records are
/// typed.
const contactRef = (c) => ({
  id: typeof c?.id === 'string' ? c.id : null,
  familyId: typeof c?.familyId === 'string' ? c.familyId : null,
  firstName: typeof c?.firstName === 'string' ? c.firstName : null,
  lastName: typeof c?.lastName === 'string' ? c.lastName : null,
  isInvoiceRecipient: Boolean(c?.isInvoiceRecipient),
});

/// The one contact a family's invoices and reminders are addressed to (#319).
///
/// `coda_domain::names::family_billing_contact` (#394), so every platform
/// answers where a studio's post goes from one implementation. Two contacts can
/// hold MMS's `Set as Preferred Invoice Recipient` at once; this takes the first
/// in list order, and an unflagged family still gets its first contact. The
/// reasoning is at that function.
///
/// Every recipient address and greeting name comes through this one function,
/// so the address a message is sent to and the name it opens with cannot come
/// from two different people. Answers null only when the family has no
/// contacts at all.
export function familyBillingContact(contacts = [], familyId) {
  const at = coreSync().familyBillingContact(contacts.map(contactRef), familyId || null);
  return at === undefined || at === null ? null : contacts[at];
}

/// Every family's billing contact from **one** crossing, as a
/// `Map<familyId, contact>` (#401).
///
/// `familyBillingContact` above hands the whole contacts window across the wasm
/// boundary on every call, and a screen with a row per student calls it once
/// per row; the cost is the crossing itself (several milliseconds per call at
/// thousands of contacts), not the projection. The core answers for the whole
/// collection and this crosses once.
///
/// The per-family function stays for callers that ask about one family;
/// `src/data/useFamilies.jsx` chooses between them for every screen.
///
/// ── What comes back ───────────────────────────────────────────────────────
///
/// The caller's own records, resolved from positions exactly as
/// `familyBillingContact` resolves its one — see `contactRef` for why the
/// record never crosses. A family with no contacts is **absent from the map**
/// rather than mapped to null, which is the same answer: `map.get(id) ?? null`
/// and `familyBillingContact` agree for every id, including ones this studio
/// has never heard of. A contact belonging to no family is in no entry.
export function familyBillingContacts(contacts = []) {
  const answers = coreSync().familyBillingContacts(contacts.map(contactRef));
  return new Map(answers.map(({ familyId, at }) => [familyId, contacts[at]]));
}

/// Every family's contacts, in tab order, from **one** crossing, as a
/// `Map<familyId, contact[]>` (#413).
///
/// `familyBillingContacts` above with the pick taken off the end, for the
/// reminder sweep in ./reminders.js: the account-facing audience is every
/// contact (MMS BCCs them into a single message — see `familyRecipients` in
/// ./notifications.js), and the sweep needs one family's contacts per
/// occurrence per open channel. Asking `familyContacts(contacts, familyId)` for
/// each would be O(families x contacts) (#401, #402). The sweep asks this
/// instead of `familyBillingContacts`.
///
/// The client has `contactsFor` in src/data/useFamilies.jsx, which answers one
/// family at a time out of a per-family cache, for a drawer open on one family.
///
/// The caller's own records, resolved from positions exactly as
/// `familyBillingContacts` resolves its one — see `contactRef`. A family with
/// no contacts is **absent from the map** rather than mapped to an empty array,
/// so `map.get(id) ?? []` and `familyContacts(contacts, id)` agree for every
/// id, including ones this studio has never heard of. A contact belonging to no
/// family is in no entry.
export function familyContactsByFamily(contacts = []) {
  const answers = coreSync().familyContactsByFamily(contacts.map(contactRef));
  return new Map(answers.map(({ familyId, at }) => [familyId, at.map((i) => contacts[i])]));
}

/// The six fields a family document carried a contact in, before #319.
///
/// Exported because two things need the same list: `planFamilyContactMigration`
/// below decides what to read off an old document, and
/// `scripts/migrate-family-contacts.mjs` asks Firestore to delete exactly these
/// field paths.
///
/// `normaliseFamily` does not write any of them, and `validFamily` in
/// firestore.rules does not name them — so they can only be on a document
/// written before #319.
export const INLINE_FAMILY_CONTACT_FIELDS = Object.freeze([
  'contactFirstName', 'contactLastName', 'email', 'phone', 'smsCapable',
  'isInvoiceRecipient',
]);

/// The contact a pre-#319 family document carries inline, as a contact record —
/// or null when it carries nobody.
///
/// **Nobody** means no name, no email and no number. A document with only
/// `isInvoiceRecipient: true` on it is a family somebody made and never filled
/// in, and would otherwise become a card reading "Contact".
///
/// `mobile`, not `phone`: the inline number is the one an invoice reminder was
/// texted to, which on a contact record is the mobile (`contact_recipient` in
/// core/src/messages.rs reads `mobile`/`mobileSmsCapable`).
///
/// **`isInvoiceRecipient` is carried over rather than forced true.**
/// `familyBillingContact` falls back to the first contact when none is
/// flagged, so "a family always has a recipient" holds without the override.
///
/// Normalised first and asked about second: an empty name is null in the
/// record, and a number with nothing left after the strip is null too.
export function inlineFamilyContact(family = {}) {
  const contact = normaliseFamilyContact({
    familyId: family.id,
    firstName: family.contactFirstName,
    lastName: family.contactLastName,
    email: family.email,
    mobile: family.phone,
    mobileSmsCapable: Boolean(family.smsCapable),
    isInvoiceRecipient: family.isInvoiceRecipient !== false,
  });
  if (!contact.firstName && !contact.lastName && !contact.email && !contact.mobile) return null;
  return contact;
}

/// Two records that are the same person, for the migration's re-run guard.
///
/// Name and email, case- and space-insensitively. **Not the phone**, because a
/// teacher who fixed a number on the migrated contact would otherwise get a
/// second copy of themselves on the next run — and not the id, because the
/// migrated record's id is minted fresh and has nothing to match against.
const sameContact = (a, b) => ['firstName', 'lastName', 'email'].every(
  (k) => String(a?.[k] ?? '').trim().toLowerCase() === String(b?.[k] ?? '').trim().toLowerCase(),
);

/// What `scripts/migrate-family-contacts.mjs` would do, as a value (#319).
///
/// Pure, and separate from the script as `planTagMigration` is: the judgement —
/// who is a duplicate, what is empty, which documents still carry a retired
/// column — is pinned by a test, and the script is REST calls over it.
///
/// Four outcomes per family, and every family lands in exactly one of the first
/// three plus, independently, `strips`:
///
///   * **`creates`** — this family carries a person inline and the collection
///     has nobody matching them. One new `familyContacts` record.
///   * **`already`** — the collection already has that person. A second run
///     after a half-applied first one lands here, which is what makes this safe
///     to run twice.
///   * **`empty`** — the six fields carry no name, no email and no number.
///   * **`strips`** — the family document still has at least one of the six on
///     it, whichever of the three above applies, so a *third* run proposes
///     nothing at all.
///
/// **A create and a strip are one family's migration and must be applied in
/// that order.** Stripping first loses the person if the create then fails;
/// creating first and failing to strip leaves them drawn twice until the next
/// run, which is recoverable. The script does them in that order per family.
export function planFamilyContactMigration({ families = [], contacts = [] } = {}) {
  const creates = [];
  const already = [];
  const empty = [];
  const strips = [];

  for (const family of families) {
    if (!family?.id) continue;
    const fields = INLINE_FAMILY_CONTACT_FIELDS.filter(
      (k) => Object.prototype.hasOwnProperty.call(family, k) && family[k] != null,
    );
    if (fields.length) strips.push({ familyId: family.id, fields });

    const inline = inlineFamilyContact(family);
    if (!inline) {
      if (fields.length) empty.push({ familyId: family.id, fields });
      continue;
    }
    const match = familyContacts(contacts, family.id).find((c) => sameContact(c, inline));
    if (match) already.push({ familyId: family.id, contactId: match.id });
    else creates.push({ familyId: family.id, contact: inline });
  }

  return {
    creates,
    already,
    empty,
    strips,
    counts: {
      families: families.length,
      creates: creates.length,
      already: already.length,
      empty: empty.length,
      strips: strips.length,
    },
  };
}

// ── Deleting a student, which mostly does not delete one (#175) ─────────────
//
// **MyMusicStaff's delete is conditional.** Its confirm says:
//
//     Are you sure you want to delete the selected students? (Students with
//     lesson/billing history will automatically be switched to 'Inactive' so
//     that this information is retained for historical reporting. All
//     'Inactive' students with lesson/billing history will remain 'Inactive').
//
// A student with lesson or billing history is not removed; they become
// `inactive`. Only a student with no history at all is really deleted. The
// teacher is told in the confirm rather than stopped.
//
// So **nothing is ever orphaned**, because the document every
// `ledgerEntries`, `attendance`, `packageEntries`, `makeupEntries`,
// `practiceEntries`, `repertoire` and `eventSeries.studentIds` reference
// points at does not go away while any of them exist.
//
// **`tags[].studentIds` is the one exception, kept by removal rather than by
// retention** (#320). A tag is a grouping a teacher maintains, not a record of
// anything that happened, so a student whose only trace is a "Trial" tag is
// deletable: `planTagDetachment` below says which memberships a delete has to
// strip, and `deleteStudents` strips them in the same batch.
//
// **That promise depends on the caller's predicate.** History is not read off
// the page's in-memory windows, because a window bounded at 5,000 rows that hit
// its limit is indistinguishable from a complete one (#318).
// `src/data/useStudents.jsx` asks the server one `limit(1)` question per
// collection per student — see `STUDENT_REFERENCES` there, where a new
// student-referencing collection has to be added. A `limit(1)` cannot be
// truncated into a false negative.
//
// The behaviour mirrored is MMS's own description in the confirm (#175).
//
// **Where we knowingly differ from MyMusicStaff** (#321): a student whose only
// trace is a scheduled, never-marked lesson is *hard-deleted* there, and the
// lesson survives them on the calendar. Ours retires that student instead. The
// argument is at `STUDENT_REFERENCES` in src/data/useStudents.jsx.
//
// **A family whose last student goes is untouched (#175).** It is a billing
// entity in its own right, and deleting it would orphan the ledger rows and
// invoices pointing at it, so `planStudentDeletion` says nothing about
// families (see `deleteStudents` in src/data/useStudents.jsx). Removing a
// family is its own act — `planFamilyDeletion` below (#346).

/// MMS's confirm, verbatim, including its parentheses and its inconsistent
/// quoting. Exported so the dialog and its test cannot drift from each other.
export const DELETE_STUDENTS_TITLE = 'Confirm Delete';
export const DELETE_STUDENTS_MESSAGE =
  'Are you sure you want to delete the selected students? (Students with '
  + "lesson/billing history will automatically be switched to 'Inactive' so that "
  + "this information is retained for historical reporting. All 'Inactive' "
  + "students with lesson/billing history will remain 'Inactive').";

/// Split a selection into the students a delete really removes and the ones it
/// retires instead.
///
/// `hasHistory` is handed in rather than computed: history lives in the
/// collections of `STUDENT_REFERENCES` in `src/data/useStudents.jsx`, asked by a
/// server query per collection (#318).
///
/// It answers `true`, `false`, or **`null` for "cannot tell"** — and `null`
/// retires. Retiring a history-free student is a status a teacher can change
/// back in the drawer; deleting one who had history is unrecoverable (#175).
///
/// `uncertain` is the subset of `retire` that got there by not knowing, so a
/// caller can say so: `StudentsPage` renders `uncertainDeletionMessage` below
/// after a delete whose probe could not answer (#408).
///
/// **The split is `people::plan_student_deletion`**, so a phone's delete
/// retires and removes exactly as this one does. The predicate stays a
/// function on this side — it is a server probe, and the core has no server —
/// and is asked once per id, in order; anything but an explicit `true` or
/// `false` crosses as "could not tell".
export function planStudentDeletion(ids = [], hasHistory = () => null) {
  const answers = ids.map((id) => {
    const known = hasHistory(id);
    return { studentId: id, hasHistory: known === true || known === false ? known : null };
  });
  return coreSync().planStudentDeletion(answers);
}

/// What a teacher is told when the delete could not check (#408).
///
/// The case: a teacher presses Delete on a student with no history, a
/// `getDocsFromServer` rejects `unavailable` one second into a reconnect, the
/// probe answers `null`, and the plan retires — an outcome that differs from
/// the one they asked for.
///
/// `probeStudentHistory` re-asks the transient before it gives up
/// (src/data/useStudents.jsx); a client that stays down still ends at *cannot
/// tell*.
///
/// **It says which outcome happened**: that the check failed, that the students
/// were switched to Inactive, and that this is reversible from the student
/// drawer.
///
/// @param count    how many students got to `retire` by not knowing.
/// @param because  `'history'` — the per-student probe did not answer — or
///                 `'tags'`, where the tag sweep did not answer and therefore
///                 held **everybody** back. Different sentences, because the
///                 second is not a fact about those students at all.
///
/// Returns `null` when there is nothing to say, so a caller renders nothing
/// rather than an empty banner.
///
/// The sentence is `people::uncertain_deletion_message`'s, so the phone says
/// the same thing after the same failure.
export function uncertainDeletionMessage(count = 0, because = 'history') {
  if (!count) return null;
  return coreSync().uncertainDeletionMessage(count, because) ?? null;
}

/// What the console says when one of the delete's reads gave up (#408).
///
/// In the domain rather than beside the query because `src/data/useStudents.jsx`
/// reaches `src/lib/firebase` and cannot be loaded by the `ui` or `domain`
/// projects, so its wording could only be checked by `integration` (#292).
///
/// **The last error is not the whole story.** `askWithRetry` returns the last
/// error, so `unavailable` → retry → `permission-denied` — an ordinary token
/// refresh during a reconnect — ends on a code that is not the transient after
/// a read that was asked four times. `attempts` is that second fact, carried
/// out of the retry loop rather than inferred from the error.
///
/// @param what        the read, named for whoever is reading the log.
/// @param code        the **last** error's Firestore code, or null.
/// @param attempts    how many times the read was actually issued.
/// @param tries       the budget `attempts` was spent out of.
/// @param waitMs      the pause between attempts, for the elapsed figure.
/// @param consequence the caller's, because they differ: a history probe that
///                    cannot tell retires **one** student, and a tag sweep that
///                    cannot tell retires the **whole selection**.
export function unansweredReadMessage({
  what = 'a read', code = null, consequence = '',
  attempts = 1, tries = 1, waitMs = 0, transientCode = 'unavailable',
}) {
  const transient = code === transientCode;
  let how;
  if (transient) {
    // Said in full rather than as "gave up": the line is the only record that
    // the retry happened (#408).
    how = ` — the transient, asked ${attempts} time(s) over ${(attempts - 1) * waitMs} ms of a `
      + `${tries}-attempt budget and still unanswered. `;
  } else if (attempts > 1) {
    // The read *was* re-asked; it is the last attempt that was not the
    // transient, and that is all this knows.
    how = ` — the **last** attempt was not the transient. This was asked ${attempts} time(s): the `
      + `earlier ones rejected as \`${transientCode}\` and were re-asked, and this one did not, so `
      + 'it stopped here. This cannot say whether that last read was refused or the query was '
      + 'malformed; it knows only that the collection did not answer. ';
  } else {
    // It cannot say why and does not pretend to: a refused read and a malformed
    // query both land here, and this knows only that the collection did not
    // answer.
    how = ' — not the transient on the first attempt, so it was not re-asked. This cannot say '
      + 'whether the read was refused or the query was malformed; it knows only that the '
      + 'collection did not answer. ';
  }
  return `[students] ${what}: ${code || 'no code'}${how}${consequence}`;
}

/// Which tag memberships a delete has to strip, and whose (#320).
///
/// `studentIds` lives on the tag rather than `tagIds` on the student (see
/// COLLECTIONS.tags in src/data/paths.js), so removing a student document would
/// leave a tag naming a student that no longer exists. The delete writes the
/// tags too, and this is the pure half: given the tags a query found and the
/// ids being removed, it returns one entry per tag that needs a write.
///
/// **Grouped by tag rather than by student.** Deleting four students who share
/// one tag is one `arrayRemove(a, b, c, d)` on one document. Four writes to the
/// same path in one batch are legal (`core/src/queue.rs` applies a repeated
/// path in order) but spend four of the batch's 500 slots on one document.
///
/// Tags with nothing to remove produce no entry, so a delete of students who
/// are in no tag writes no tag documents at all.
///
/// **The grouping is `people::plan_tag_detachment`.** A tag crosses as
/// its memberships — one `{ tag, tagId, studentId }` row each, `tag` being its
/// position here so two tags sharing an id stay two writes — because the flat
/// rule keeps a list out of a record. Student ids are de-duplicated there:
/// `arrayRemove` takes a list of elements, and a tag holding the same id twice
/// is a document nothing here wrote but the rules do not forbid. A member that
/// is not a string is no member, since no removed id could equal it.
export function planTagDetachment(tags = [], removedIds = []) {
  const members = [];
  tags.forEach((tag, at) => {
    if (!tag || !tag.id) return;
    const ids = Array.isArray(tag.studentIds) ? tag.studentIds : [];
    for (const studentId of ids) {
      if (typeof studentId === 'string') members.push({ tag: at, tagId: String(tag.id), studentId });
    }
  });
  const removed = Array.from(removedIds).filter((id) => typeof id === 'string');
  return coreSync().planTagDetachment(members, removed);
}

// ── Deleting a family (#346) ────────────────────────────────────────────────
//
// A student delete leaves the family alone (above), so removing a family is
// its own act, from Families & Invoices.
//
// How MMS deletes a family is not known (#346, #286, #306;
// `research/mms-capture/` does not cover it), so the behaviour below is ours.
//
// ── Refuse, rather than retire ─────────────────────────────────────────────
//
// **A family has no status field** to retire it with, so there are two
// outcomes — remove it, or refuse and say what is holding it. A family wrongly
// removed takes the invoice recipient and the billing name off every ledger row
// and invoice pointing at it.
//
// **`null` — could not tell — refuses too**, since there is nothing reversible
// to fall back on. `probeFamilyReferences` in src/data/useFamilies.jsx answers
// `null` for a collection that did not respond, and this treats it as held
// while naming it separately, so a teacher is told "we could not check" rather
// than "you have transactions".
//
// ── What counts as a reference is the caller's list ────────────────────────
//
// `FAMILY_REFERENCES` in src/data/useFamilies.jsx, the same shape as
// `STUDENT_REFERENCES`. This module takes the answers and nothing else.
//
// **Contacts cascade**, as tag membership does above (#320): a contact is the
// family's own people rather than a record of something that happened. They
// are not in the list, and the caller deletes them in the same batch.

/// The copy on the confirm. **Ours, not MMS's** — see the header above.
export const DELETE_FAMILY_TITLE = 'Confirm Delete';
export const DELETE_FAMILY_MESSAGE =
  'Are you sure you want to delete this family account? A family that has any '
  + 'students, transactions, invoices or lesson history cannot be deleted; its '
  + 'contacts are deleted with it.';

/// Can this family be removed, and if not, what is holding it?
///
/// `answers` is one entry per reference — `{ label, found }` — where `found` is
/// `true`, `false`, or **`null` for "could not tell"**. `label` is the noun a
/// teacher reads in the refusal, so it is the caller's to phrase.
///
/// Returns `{ deletable, held, unknown }`. `held` and `unknown` are disjoint
/// label lists, and `deletable` is true only when both are empty — a family is
/// removed on a complete set of noes and on nothing else.
export function planFamilyDeletion(answers = []) {
  // Anything that is not an explicit true or false — `null`, `undefined`, a
  // predicate that fell off the end — is "could not tell", as in
  // `planStudentDeletion`. The split itself is `people::plan_family_deletion`.
  const rows = [];
  for (const a of answers) {
    if (!a || !a.label) continue;
    rows.push({ label: String(a.label), found: a.found === true || a.found === false ? a.found : null });
  }
  return coreSync().planFamilyDeletion(rows);
}

/// The sentence a teacher is shown when `planFamilyDeletion` refuses.
///
/// `null` when the plan is deletable, so a caller can use it as the switch for
/// whether to refuse at all rather than keeping a second predicate beside it.
///
/// **The two halves are kept apart**: "this family has 2 transactions" and "we
/// could not reach transactions" are different facts, and folding them would
/// tell a teacher they have history they may not have. The sentence, and its
/// Oxford-less `a, b and c`, is `people::family_deletion_refusal`'s.
export function familyDeletionRefusal(plan = {}) {
  return coreSync().familyDeletionRefusal(plan.held || [], plan.unknown || []) ?? null;
}

// The family is the billing entity. `lastName` is the family's own name — what
// the invoice is addressed to and what the families list shows — and is not the
// same thing as any contact person's name, even though a new family defaults
// one from the other.
//
// ── It carries no person (#319) ─────────────────────────────────────────────
//
// A family's contacts are `familyContacts` records: `familyBillingContact`
// above resolves the recipient from the collection, and functions/reminders/
// and `functions/delivery/firestoreStore.js` read it there. A pre-#319
// document may still carry `contactFirstName`, `contactLastName`, `email`,
// `phone`, `smsCapable` and `isInvoiceRecipient` inline.
//
// **Anything not listed here is dropped rather than persisted**, so a caller
// that passes `contactFirstName` writes nothing. `firestore.rules` does not
// name the six either, and `validFamily` has no `keys().hasOnly` (the note
// there says why), so an old document that still carries them can still be
// updated.
//
// The two fields and what an empty one stores are `people::normalise_family`;
// `timeZone` is explained below.
export function normaliseFamily(input = {}) {
  return documentFromRows(coreSync().normaliseFamily(inputOf(FAMILY_INPUT, input, hasOwn)));
}

// ── `timeZone`, and why it is the family's ─────────────────────────────────
//
// What clock the people on this account read, for the messages addressed to
// them. Display only, and null means "the studio's" (domain/timeZone.js).
//
// **On the family, not on the student, because that is where MMS puts it.**
// With Multi-Time Zone Selection turned on, MMS offers the field in two places
// — the Add New Student wizard's *Family Details* step, and the first row of a
// student's Edit drawer — and both carry the same sentence under it:
//
//     This time zone will also apply to all contacts and Students in this
//     student's Family.
//
// So it is one zone per account, reachable from a student, and reading it off a
// student is reading the family's. See research/mms-capture/06-multi-timezone/.
// One reminder goes to one family, so two siblings cannot disagree.
//
// It changes nothing about *when* the lesson is: a recurring lesson is anchored
// in the zone its series was created in, as in MMS, where moving a student's
// zone leaves the teacher's calendar unchanged. It decides what the messages
// say: a reminder to a family in Seoul about a New York lesson prints their
// five in the morning beside the studio's four in the afternoon.
//
// Not checked against a list of real zones, as normaliseStudio does not check
// the studio's: the tz database gains and renames them, and the formatter
// refuses a zone it cannot resolve.

// The three fields the name rules read, and nothing else.
//
// A projection rather than the whole document, so only what the rules read
// crosses (`core-ffi`'s `StudentName`). `undefined` for an absent person,
// which is what the boundary's `Option` wants.
const nameOf = (student) => (student ? {
  firstName: student.firstName ?? null,
  lastName: student.lastName ?? null,
  email: student.email ?? null,
} : undefined);

// The same projection for a family, assembled from two documents (#319): the
// family's own `lastName`, and the **billing contact's** first and last name
// out of `familyContacts`. The core's `FamilyName` record does not care where
// the two halves came from. `contact` is optional because
// `familyDisplayName` reads only `lastName`; a caller naming the account has
// nothing to supply.
const familyNameOf = (family, contact = null) => (family ? {
  lastName: family.lastName ?? null,
  contactFirstName: contact?.firstName ?? null,
  contactLastName: contact?.lastName ?? null,
} : undefined);

// ── How a person is named is `core/src/names.rs` (#214) ────────────────────
//
// The rules are the core's; the **defaults** are here. Each of these five
// functions supplies the argument its call sites omit and then asks the core.
//
// The boundary takes `fallback` as a required `String` and `format` as an
// `Option`, because a core has no view of what a nameless student is called on
// *our* screens. Keeping the default here, rather than at every call site,
// keeps one answer, as `useNames()` does one layer up for the *format*.
//
// An absent format resolves to `last_comma_first` on both sides, an
// unrecognised one falls back rather than erroring on both sides, and
// `familyContactName`'s first-last default is the core's too.

// **`fallback` may be `null`, and that is a contract.**
// `functions/delivery/templates.js` passes `studentDisplayName(s, null)` inside
// a `.map().filter(Boolean)`, and `familyDisplayName(family, null)` for a table
// row that is dropped when empty: "no fallback: give me nothing if there is no
// name".
//
// `fallback` crosses as a required `String`, so `null` would reach
// `passStringToWasm0` and throw on `.length`. So the empty string goes across,
// and `|| fallback` turns the empty answer back into whatever the caller meant
// by absence.

// The way every list renders a student. Derived on read rather than stored, so
// renaming a student cannot leave a stored copy behind.
//
// `format` is the studio's Student Name Format, and it is the *third* argument
// because `fallback` is the second and is passed positionally from
// functions/delivery/templates.js, which runs the generated mirror of this
// file.
export function studentDisplayName(student, fallback = 'Student', format = DEFAULT_NAME_FORMATS.student) {
  return coreSync().studentDisplayName(nameOf(student), fallback ?? '', format) || fallback;
}

// Sorting and searching want different strings from display, and getting them
// from the display name would be wrong: the comma would sort between letters,
// and searching "ada okafor" should match a name rendered "Okafor, Ada".
//
// The sort key does follow the format, though. A list rendered "Ada Okafor" but
// ordered by Okafor reads as unsorted, which would make the setting cosmetic.
export function studentSortKey(student, format = DEFAULT_NAME_FORMATS.student) {
  return coreSync().studentSortKey(nameOf(student), format);
}

// **Not** format-aware, unlike the sort key: what a search matches must not
// change when the studio changes how names are written.
export function studentSearchText(student) {
  return coreSync().studentSearchText(nameOf(student));
}

// The person a family account's messages are addressed to. Falls back to the
// family name, which is what a family with no contact on file has.
//
// The default is "First Last", *not* the studio's Parent Name Format, because
// the callers that do not pass one are not lists: functions/delivery/templates.js
// opens every message with it ("Hi Ada Okafor,"). The screens that list parents
// pass the studio's format (see hooks/useNames.js).
//
// ── `contact` is the second argument (#319) ────────────────────────────────
//
// The contact is a `familyContacts` record, so the caller supplies it.
// `contact` is second because it is required, and `fallback` and `format` keep
// their order after it; every call site passes `fallback` positionally.
export function billingContactName(
  family, contact = null, fallback = 'Contact', format = NAME_FORMATS.firstLast,
) {
  return coreSync().familyContactName(familyNameOf(family, contact), fallback ?? '', format)
    || fallback;
}

// What the families list and invoices call the account.
//
// Not format-aware: this is the family's own single name — what the invoice
// is addressed to — not a first and last name to be ordered.
export function familyDisplayName(family, fallback = 'Family') {
  return coreSync().familyDisplayName(familyNameOf(family), fallback ?? '') || fallback;
}

// ── The Add Student wizard's Family step (core/src/add_student.rs) ─────────
//
// Whether the step asks for a parent, when it may be left, and who a new
// family is made from, one rule for the three wizards. As in MMS, an adult
// student joining a new family is their own family, and the step asks for
// nothing more. What MMS does, and where we knowingly differ, is the core
// module's header.
//
// The form crosses as the record the core reads — every field text or a tick,
// coerced the way this file coerces a form — so a caller hands over the
// wizard's whole `form` and nothing is renamed on the way but the zone.
const FAMILY_STEP_TEXT = [
  'studentType', 'familyChoice', 'existingFamilyId', 'firstName', 'lastName', 'email', 'phone',
  'parentFirstName', 'parentLastName', 'parentEmail', 'parentPhone',
];
const familyStepForm = (form = {}) => ({
  ...Object.fromEntries(FAMILY_STEP_TEXT.map((k) => [k, asText(form[k])])),
  smsCapable: Boolean(form.smsCapable),
  parentSms: Boolean(form.parentSms),
  // The wizard calls the family's zone `familyTimeZone`, after the field it
  // fills; the record calls it what the family document does.
  timeZone: asText(form.familyTimeZone),
});

// Whether the Family step shows the parent's fields.
export function addStudentAsksForParent(form) {
  return coreSync().addStudentAsksForParent(familyStepForm(form));
}

// Whether the Family step may be left — and, re-asked by Save, whether what
// it holds can be written.
export function addStudentFamilyStepReady(form) {
  return coreSync().addStudentFamilyStepReady(familyStepForm(form));
}

// `createStudent`'s `family` and `contact` for a new family, or two nulls for
// an existing one.
export function addStudentNewFamily(form) {
  const made = coreSync().addStudentNewFamily(familyStepForm(form));
  if (!made) return { family: null, contact: null };
  return {
    family: { lastName: made.familyLastName, timeZone: made.timeZone },
    contact: {
      firstName: made.contactFirstName,
      lastName: made.contactLastName,
      email: made.contactEmail,
      mobile: made.contactMobile,
      mobileSmsCapable: made.contactMobileSmsCapable,
    },
  };
}
